import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Mail, User, Send, AlertCircle, RefreshCw } from 'lucide-react';
import api, { formatErrorMessage } from '../../../../utils/api';
import { useIdempotencyKey } from '../../../../utils/idempotency';
import { toast } from 'sonner';

/**
 * Modal para enviar estado de cuenta CONSOLIDADO por RUT.
 * El usuario debe seleccionar explícitamente un contacto entre los asociados al RUT.
 */
const EnviarEstadoCuentaConsolidadoModal = ({ open, onOpenChange, cuenta }) => {
  const [loadingContactos, setLoadingContactos] = useState(false);
  const [contactos, setContactos] = useState([]);
  const [contactoIdx, setContactoIdx] = useState(null);
  const [emailManual, setEmailManual] = useState('');
  const [nombreManual, setNombreManual] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null); // {estado, error_envio, comunicacion_id}
  const { key: idempotencyKey, reset: resetIdempotencyKey } = useIdempotencyKey(open);

  const fetchContactos = useCallback(async () => {
    if (!cuenta?.rut_normalizado) return;
    setLoadingContactos(true);
    setContactos([]);
    setContactoIdx(null);
    setEmailManual('');
    setNombreManual('');
    try {
      const r = await api.get(`/finanzas/cobranza/cuentas-consolidadas/${encodeURIComponent(cuenta.rut_normalizado)}/detalle-completo`);
      setContactos(r.data?.contactos || []);
    } catch (err) {
      toast.error(formatErrorMessage(err) || 'Error al cargar contactos');
    } finally {
      setLoadingContactos(false);
    }
  }, [cuenta]);

  useEffect(() => {
    if (open) fetchContactos();
  }, [open, fetchContactos]);

  const handleEnviar = async () => {
    let email_destino = null;
    let contacto_nombre = null;

    if (contactoIdx === 'manual') {
      if (!emailManual || !emailManual.includes('@')) {
        toast.error('Ingresa un email válido');
        return;
      }
      email_destino = emailManual;
      contacto_nombre = nombreManual || null;
    } else if (contactoIdx !== null && contactos[contactoIdx]) {
      email_destino = contactos[contactoIdx].email;
      contacto_nombre = contactos[contactoIdx].nombre;
    }

    if (!email_destino) {
      toast.error('Selecciona un contacto o ingresa un email');
      return;
    }

    setEnviando(true);
    try {
      const contacto_usado = (contactoIdx !== null && contactoIdx !== 'manual')
        ? contactos[Number(contactoIdx)]
        : { nombre: nombreManual || null, email: emailManual, origen: 'manual', rol: 'Destinatario manual' };
      const r = await api.post(
        `/finanzas/cobranza/cuentas-consolidadas/${encodeURIComponent(cuenta.rut_normalizado)}/estado-cuenta`,
        { email_destino, contacto_usado, idempotency_key: idempotencyKey }
      );
      const data = r.data || {};
      setResultado({
        estado: data.estado,
        error_envio: data.error_envio,
        comunicacion_id: data.comunicacion_id,
        duplicado_detectado: data.duplicado_detectado,
      });
      if (data.estado === 'no_enviado') {
        toast.error(`Envío fallido: ${data.error_envio}. La comunicación quedó registrada; puedes reintentar.`);
      } else if (data.duplicado_detectado) {
        toast.info('Solicitud ya procesada previamente. Se recuperó la comunicación existente.');
      } else {
        toast.success(data.message || 'Estado de cuenta consolidado enviado');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error('Conflicto de idempotencia: la clave ya fue usada con datos distintos. Genera una nueva solicitud.');
        resetIdempotencyKey();
      } else {
        toast.error(formatErrorMessage(err) || 'Error al enviar estado de cuenta');
      }
    } finally {
      setEnviando(false);
    }
  };

  const handleReintentar = async () => {
    if (!resultado?.comunicacion_id) return;
    setEnviando(true);
    try {
      const contacto_usado = (contactoIdx !== null && contactoIdx !== 'manual')
        ? contactos[Number(contactoIdx)]
        : { nombre: nombreManual || null, email: emailManual, origen: 'manual' };
      const email_destino = contactoIdx === 'manual' ? emailManual : contacto_usado.email;
      const r = await api.post(
        `/finanzas/cobranza/comunicaciones/${resultado.comunicacion_id}/reintentar`,
        { email_destino, contacto_usado }
      );
      const data = r.data || {};
      setResultado(prev => ({ ...prev, estado: data.estado, error_envio: data.error_envio }));
      if (data.estado === 'no_enviado') {
        toast.error(`Reintento fallido: ${data.error_envio}. Puedes intentar de nuevo.`);
      } else {
        toast.success('Reintento exitoso.');
      }
    } catch (err) {
      toast.error(formatErrorMessage(err) || 'Error al reintentar envío');
    } finally {
      setEnviando(false);
    }
  };

  const contactosValidos = contactos.filter(c => c.email);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl' data-testid='enviar-estado-consolidado-modal'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Mail className='w-5 h-5 text-blue-600' />
            Enviar Estado de Cuenta Consolidado
          </DialogTitle>
        </DialogHeader>

        {cuenta && (
          <div className='bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm'>
            <p><strong>Cliente:</strong> {cuenta.nombre_principal}</p>
            <p><strong>RUT:</strong> <span className='font-mono'>{cuenta.rut_display}</span></p>
            <p><strong>Saldo pendiente:</strong> <span className='text-red-700 font-semibold'>${new Intl.NumberFormat('es-CL').format(cuenta.saldo_total || 0)}</span></p>
            <p><strong>Facturas incluidas:</strong> {cuenta.n_facturas_pendientes || 0}</p>
          </div>
        )}

        <div className='space-y-3'>
          <Label className='text-sm font-semibold'>Selecciona el contacto destinatario:</Label>
          {loadingContactos ? (
            <p className='text-xs text-slate-500'>Cargando contactos...</p>
          ) : contactosValidos.length === 0 ? (
            <div className='bg-amber-50 border border-amber-200 rounded p-3 flex gap-2'>
              <AlertCircle className='w-4 h-4 text-amber-600 shrink-0 mt-0.5' />
              <p className='text-xs text-amber-800'>
                No hay contactos con email registrado para este RUT. Ingresa el email manualmente abajo.
              </p>
            </div>
          ) : (
            <RadioGroup
              value={contactoIdx !== null ? String(contactoIdx) : undefined}
              onValueChange={(v) => setContactoIdx(v === 'manual' ? 'manual' : Number(v))}
            >
              <div className='space-y-2 max-h-64 overflow-y-auto'>
                {contactosValidos.map((c, i) => (
                  <label
                    key={i}
                    className={`flex items-start gap-3 p-2 border rounded cursor-pointer hover:bg-slate-50 ${
                      contactoIdx === i ? 'border-blue-500 bg-blue-50' : 'border-slate-200'
                    }`}
                    data-testid={`contacto-opt-${i}`}
                  >
                    <RadioGroupItem value={String(i)} className='mt-1' />
                    <div className='flex-1'>
                      <p className='text-sm font-medium text-slate-900 flex items-center gap-2'>
                        <User className='w-3 h-3 text-slate-500' /> {c.nombre || '(sin nombre)'}
                      </p>
                      <p className='text-xs text-slate-600'>{c.email}</p>
                      <p className='text-[10px] text-slate-400'>{c.rol} · {c.origen}</p>
                    </div>
                  </label>
                ))}
              </div>
            </RadioGroup>
          )}

          <div className='pt-2 border-t border-slate-200'>
            <label className='flex items-start gap-3 p-2 border border-slate-200 rounded cursor-pointer hover:bg-slate-50'
                   data-testid='contacto-opt-manual'>
              <input
                type='radio'
                name='contacto'
                value='manual'
                checked={contactoIdx === 'manual'}
                onChange={() => setContactoIdx('manual')}
                className='mt-1'
              />
              <div className='flex-1'>
                <p className='text-sm font-medium'>Otro destinatario</p>
                {contactoIdx === 'manual' && (
                  <div className='mt-2 space-y-2'>
                    <Input
                      type='email'
                      placeholder='email@ejemplo.cl'
                      value={emailManual}
                      onChange={(e) => setEmailManual(e.target.value)}
                      className='h-8 text-sm'
                      data-testid='email-manual-input'
                    />
                    <Input
                      type='text'
                      placeholder='Nombre (opcional)'
                      value={nombreManual}
                      onChange={(e) => setNombreManual(e.target.value)}
                      className='h-8 text-sm'
                    />
                  </div>
                )}
              </div>
            </label>
          </div>
        </div>

        {resultado && (
          <div
            className={`rounded border p-3 text-xs ${
              resultado.estado === 'no_enviado'
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-green-50 border-green-200 text-green-800'
            }`}
            data-testid='envio-resultado-banner'
          >
            <p className='font-semibold'>
              Estado: {resultado.estado === 'no_enviado' ? 'No enviado' : 'Enviado (simulado)'}
              {resultado.duplicado_detectado && <span className='ml-2 text-slate-600'>(recuperado por idempotencia)</span>}
            </p>
            {resultado.error_envio && (
              <p className='mt-1'>Error: <code>{resultado.error_envio}</code></p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={() => { setResultado(null); onOpenChange(false); }}>Cerrar</Button>
          {resultado?.estado === 'no_enviado' && (
            <Button
              onClick={handleReintentar}
              disabled={enviando}
              variant='outline'
              className='border-amber-400 text-amber-700 hover:bg-amber-50'
              data-testid='reintentar-btn'
            >
              <RefreshCw className='w-4 h-4 mr-2' />
              {enviando ? 'Reintentando...' : 'Reintentar envío'}
            </Button>
          )}
          {!resultado && (
            <Button
              onClick={handleEnviar}
              disabled={enviando || contactoIdx === null}
              data-testid='enviar-estado-confirmar'
            >
              <Send className='w-4 h-4 mr-2' />
              {enviando ? 'Enviando...' : 'Enviar Estado de Cuenta'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EnviarEstadoCuentaConsolidadoModal;
